import React, { useCallback, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components/macro";
import {
  PrimaryButtonMedium,
  PrimaryButtonLargeV2,
  SecondaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../Buttons/Buttons";
import { DropDownModal } from "../DropDownModal/DropDownModal";
import { CaretDownIcon, CaretUpIcon } from "../Icons/Icons";
import { match } from "ts-pattern";
import { useOnClickOutside } from "../../util/hooks";
import { spacingTokens } from "../../designTokens";

type DropDownButtonProps = {
  items: (number | string)[];
  activeItem?: number | string;
  textLeft: string;
  clickHandler: (item: string) => void;
  className?: string;
  isDisabled?: boolean;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  variant?: "primary" | "secondary" | "tertiary";
};

const DropDownScrollableModal = styled(DropDownModal)`
  max-height: 200px;
  overflow-y: auto;
`;

const DropDownButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const DropDownModalWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 100%;
  z-index: 100;
`;

/**
 * DropDownButton - A button with a dropdown menu
 *
 * Usage:
 * - **Without `variant` prop (default)**: Uses `PrimaryButtonMedium` for backward compatibility.
 *   This is the original button style and should be used for all non-PIM pages.
 *
 * - **With `variant="primary"`**: Uses `PrimaryButtonLargeV2` (new V2 design system).
 *   Use this for primary actions in PIM pages.
 *
 * - **With `variant="secondary"`**: Uses `SecondaryButtonLargeV2` (new V2 design system).
 *   Use this for secondary actions in PIM pages.
 *
 * - **With `variant="tertiary"`**: Uses `TertiaryButtonLargeV2` (new V2 design system).
 *   Use this for tertiary actions in PIM pages.
 *
 * @example
 * // Non-PIM pages (Orders, Quotes, Leads, etc.) - use default
 * <DropDownButton items={items} textLeft="Actions" clickHandler={handler} />
 *
 * @example
 * // PIM pages - specify variant
 * <DropDownButton items={items} textLeft="Bulk Action" clickHandler={handler} variant="secondary" />
 */
export function DropDownButton({
  items,
  activeItem,
  textLeft,
  clickHandler,
  className,
  isDisabled = false,
  style,
  icon,
  variant,
}: DropDownButtonProps) {
  const [showDropDown, setShowDropDown] = useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useOnClickOutside(wrapperRef, () => setShowDropDown(false));

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const buttonText = (event.target as HTMLElement)?.textContent;
      clickHandler(buttonText ?? "");
      setShowDropDown(false);
    },
    [clickHandler]
  );

  const toggleDropDown = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDropDown((prev) => !prev);
  }, []);

  const renderCaret = useMemo(
    () =>
      match(showDropDown)
        .with(true, () => (
          <CaretUpIcon
            fill={theme.primaryButtonTextColor}
            width={21}
            height={21}
          />
        ))
        .otherwise(() => (
          <CaretDownIcon
            fill={theme.primaryButtonTextColor}
            width={21}
            height={21}
          />
        )),
    [showDropDown, theme.primaryButtonTextColor]
  );

  const Button = variant
    ? match(variant)
        .with("primary", () => PrimaryButtonLargeV2)
        .with("secondary", () => SecondaryButtonLargeV2)
        .with("tertiary", () => TertiaryButtonLargeV2)
        .exhaustive()
    : PrimaryButtonMedium;

  return (
    <DropDownButtonWrapper ref={wrapperRef} className={className}>
      <Button onClick={toggleDropDown} disabled={isDisabled} style={style}>
        <ButtonContent>
          {icon && (
            <span style={{ marginRight: "5px", height: "14px" }}>{icon}</span>
          )}
          <span style={{ marginRight: spacingTokens.spacing_md }}>
            {textLeft}
          </span>
          {renderCaret}
        </ButtonContent>
      </Button>
      {showDropDown && (
        <DropDownModalWrapper>
          <DropDownScrollableModal
            items={items}
            activeItem={activeItem}
            clickHandler={handleClick}
          />
        </DropDownModalWrapper>
      )}
    </DropDownButtonWrapper>
  );
}
